import {
  Button,
  Card,
  Checkbox,
  Container,
  MenuItem,
  Pagination,
  styled,
  useMediaQuery,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  FieldRequiredIndicator,
  IDeaNumUser,
  IDeaRegistration,
  searchEmailsMinLength,
  searchUserByFirstNameMinLength,
  searchUserByLastNameMinLength,
  StyledAutocomplete2,
  StyledSelect,
  Tooltip,
  TooltipTrigger,
  useDeaRegistrationSearch,
  useEmailSearch,
  useSerialNumberSearch,
  useFirstNameSearch,
  useLastNameSearch,
  useToggle,
  ValueLabel,
  roleLabel,
  roles,
  variables,
  useRoleOpContext,
  disableOperationsHelpDesk,
  RevokeConsent,
  useAlertContext,
  side,
  IRevokeRoles,
  useUserContext,
  ResponseCSOSHeaders,
  revokeRoles,
  IError,
  ErrorCode,
  deaNumUsersAllSelected,
} from '../common';
import config from '../config.json';
import { uniq, pathOr, always, split, trim, assocPath } from 'rambda';
import { Justification, justificationOptions } from '../request';
import { Dispatch, useState } from 'react';
import {
  disableSubmit,
  ITeamRequestState,
  saveRequestStatus,
  StatusSaveRequst,
  TeamRequestAction,
  UserRoles2d,
  UserRolesStack,
} from '.';
import {
  compose as lcompose,
  map as lmap,
  orderBy as lorderBy,
  uniqWith as luniqWith,
  keys,
  filter as lfilter,
  eq as leq,
  negate as lnegate,
  propOr as lpropOr,
  tap as ltap,
  trim as ltrim,
  getOr,
  isEmpty as lisEmpty,
  pick as lpick,
} from 'lodash/fp';
import { fold, left, right, map as emap } from 'fp-ts/Either';
import { flow } from 'fp-ts/function';

const properties: ValueLabel<keyof IDeaNumUser>[] = [
  { value: 'deaNumber', label: 'DEA Registration Number' },
  { value: 'serialNumber', label: 'Serial Number' },
  { value: 'role', label: 'Role' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'loginEmail', label: 'Email' },
];

export const TeamRequest = ({
  teamRequestState,
  dispatchTeamRequest,
}: {
  teamRequestState: ITeamRequestState;
  dispatchTeamRequest: Dispatch<TeamRequestAction>;
}) => {
  const matchesGridBreakpoint = useMediaQuery('(min-width:60em)', {
    noSsr: true,
  });

  const {
    value: tooltipValue,
    toggle: toggleTooltip,
    off: offTooltip,
  } = useToggle({
    init: lcompose(
      lmap<string, ValueLabel<boolean>>(label => ({ label, value: false })),
      keys
    )(config),
  });

  const { emails, emailInput, setEmailInput } = useEmailSearch();
  const { serialNumbers, serialNumberInput, setSerialNumberInput } =
    useSerialNumberSearch();
  const { users, firstNameSearchInput, setFirstNameSearchInput } =
    useFirstNameSearch();
  const {
    users: usersByLastName,
    lastNameSearchInput,
    setLastNameSearchInput,
  } = useLastNameSearch();

  const {
    deaRegistrations,
    deaRegistrationSearchInput,
    setDeaRegistrationSearchInput,
  } = useDeaRegistrationSearch();

  const deaNumUsers = lcompose(
    lmap<IDeaNumUser, IDeaNumUser>(a => ({
      ...a,
      role: roleLabel(roles)(a.role),
    })),
    lorderBy<IDeaNumUser>(['deaNumber'], ['asc']),
    lfilter<IDeaNumUser>(lcompose(lnegate(leq('ADM')), pathOr('', 'role'))),
    luniqWith(
      (a: IDeaNumUser, b: IDeaNumUser) =>
        a.deaNumber === b.deaNumber &&
        a.role === b.role &&
        a.userProfileId === b.userProfileId
    ),
    pathOr([], 'deanumUsers')
  );

  const setFirstNameLastName = lcompose(
    ltap(
      lcompose(
        dispatchTeamRequest,
        payload => ({
          type: 'SetFirstName',
          payload,
        }),
        lcompose(trim, getOr('', '[1]'), split(','))
      )
    ),
    ltap(
      lcompose(
        dispatchTeamRequest,
        payload => ({
          type: 'SetLastName',
          payload,
        }),
        lcompose(trim, getOr('', '[0]'), split(','))
      )
    )
  );

  const clearFirstNameLastName = lcompose(
    dispatchTeamRequest,
    always({ type: 'SetFirstName', payload: null }),
    dispatchTeamRequest,
    always({ type: 'SetLastName', payload: null })
  );

  const [page, setPage] = useState(1);
  const pageSize = 100;

  const { operations } = useRoleOpContext();
  const disableOperationsHelpDsk = disableOperationsHelpDesk(operations);

  // submit

  const { user } = useUserContext();
  const { setAlert } = useAlertContext();
  const handleConfirm = () =>
    setAlert(a => ({
      ...a,
      title: 'CSOS Submit Request',
      message: () => <RevokeConsent handleClose={handleCloseConfirm} />,
      okAction: false,
      cancelAction: false,
      isOpen: true,
    }));
  const handleCloseConfirm = lcompose(
    side((a: boolean) => setAlert(b => ({ ...b, isOpen: false, isOk: a }))),
    side((a: boolean) =>
      a
        ? lcompose(
            emap<IRevokeRoles, Promise<void>>(
              handleRequest(user.icam!.headers)(dispatchTeamRequest)
            ),
            getRequest
          )(teamRequestState)
        : void 0
    )
  );
  const getRequest = (requestState: ITeamRequestState) => {
    return lcompose(
      right,

      assocPath(
        'requestorComments',
        lcompose(
          a =>
            a === 'Other'
              ? pathOr('', 'request.otherComments')(requestState)
              : a,
          pathOr('', 'request.comments')
        )(requestState)
      ),

      assocPath(
        'revokeList',
        lcompose(
          lmap<
            IDeaNumUser,
            { deaInfoId: number; serialNumber: string; userProfileId: number }
          >(lpick(['deaInfoId', 'serialNumber', 'userProfileId'])),
          lfilter<IDeaNumUser>(({ key }) =>
            requestState.selectedUserRoleKeys.includes(key)
          ),
          pathOr([], 'deanumUsers')
        )(requestState)
      )
    )({});
  };
  const handleRequest =
    (hR: ResponseCSOSHeaders) =>
    (dispatchRequest: Dispatch<TeamRequestAction>) =>
    (request: IRevokeRoles) =>
      revokeRoles(hR)(request).then(
        fold<IError, void, void>(
          () =>
            dispatchRequest({
              type: 'SaveRequestError',
              payload: { code: ErrorCode.SaveRequestError, message: '' },
            }),
          b => {
            dispatchRequest({
              type: 'SaveRequestSuccess',
              payload: void 0,
            });
          }
        )
      );

  return (
    <Wrapper maxWidth="xl">
      <Card>
        <Actions>
          <ActionLabel id="request-type">Request Type * :</ActionLabel>

          <Tooltip name="requestType" tooltipValue={tooltipValue}>
            <StyledSelect
              labelId="request-type"
              value={`REVOKE`}
              variant="outlined">
              <MenuItem value="REVOKE">Revoke</MenuItem>
            </StyledSelect>
          </Tooltip>
          <TooltipTrigger
            name="requestType"
            toggleTooltip={toggleTooltip}
            offTooltip={offTooltip}
          />

          <Justification
            value={teamRequestState.request.comments || ''}
            options={justificationOptions.REVOKE}
            onChange={(payload: string) =>
              dispatchTeamRequest({ type: 'SetJustification', payload })
            }
            otherComments={teamRequestState.request.otherComments}
            onChangeOtherComments={(payload: string) =>
              dispatchTeamRequest({ type: 'SetOtherComments', payload })
            }
            tooltipValue={tooltipValue}
            toggleTooltip={toggleTooltip}
            offTooltip={offTooltip}
          />
        </Actions>
      </Card>
      <SectionWrapper>
        <h3>Search User Certificates</h3>
        <FilterWrapper>
          <ActionLabel id="first-name">First Name:</ActionLabel>
          <StyledAutocomplete2
            value={teamRequestState.firstName}
            labelledby="first-name"
            inputValue={firstNameSearchInput}
            onInputChange={lcompose(
              ltap(
                lcompose(emap(clearFirstNameLastName), (a: string) =>
                  a === '' && teamRequestState.firstName !== null
                    ? right(void 0)
                    : left(void 0)
                )
              ),
              ltap(setFirstNameSearchInput),
              ltrim
            )}
            options={users}
            onChange={lcompose(
              () => setSerialNumberInput(''),
              () => setDeaRegistrationSearchInput(''),
              () => setEmailInput(''),

              setFirstNameLastName
            )}
            noOptionsText={
              firstNameSearchInput.length < searchUserByFirstNameMinLength
                ? `Please enter minimum ${searchUserByFirstNameMinLength} leading characters to initiate search...`
                : 'No results...'
            }
          />

          <ActionLabel id="last-name">Last Name:</ActionLabel>
          <StyledAutocomplete2
            value={teamRequestState.lastName}
            labelledby="last-name"
            inputValue={lastNameSearchInput}
            onInputChange={lcompose(
              ltap(
                lcompose(emap(clearFirstNameLastName), (a: string) =>
                  a === '' && teamRequestState.lastName !== null
                    ? right(void 0)
                    : left(void 0)
                )
              ),
              ltap(setLastNameSearchInput),
              ltrim
            )}
            options={usersByLastName}
            onChange={lcompose(
              () => setSerialNumberInput(''),
              () => setDeaRegistrationSearchInput(''),
              () => setEmailInput(''),

              setFirstNameLastName
            )}
            noOptionsText={
              lastNameSearchInput.length < searchUserByLastNameMinLength
                ? `Please enter minimum ${searchUserByLastNameMinLength} leading characters to initiate search...`
                : 'No results...'
            }
          />

          <ActionLabel id="email">Email:</ActionLabel>
          <StyledAutocomplete2
            value={teamRequestState.email}
            labelledby="email"
            inputValue={emailInput}
            onInputChange={lcompose(
              ltap(
                lcompose(
                  emap(
                    lcompose(
                      dispatchTeamRequest,
                      always({ type: 'SetEmail', payload: null })
                    )
                  ),
                  (a: string) => (a === '' ? right(void 0) : left(void 0))
                )
              ),
              ltap(setEmailInput),
              ltrim
            )}
            options={emails}
            onChange={lcompose(
              () => setSerialNumberInput(''),
              () => setDeaRegistrationSearchInput(''),
              value => dispatchTeamRequest({ type: 'SetEmail', payload: value })
            )}
            noOptionsText={
              emailInput.length < searchEmailsMinLength
                ? `Please enter minimum ${searchEmailsMinLength} leading characters to initiate search...`
                : 'No results...'
            }
          />
          <FilterLabel id="deaNum">DEA Registration Number:</FilterLabel>
          <StyledAutocomplete2
            value={teamRequestState.deaNum}
            labelledby="deaNum"
            inputValue={deaRegistrationSearchInput}
            onInputChange={lcompose(
              ltap(
                lcompose(
                  emap(
                    lcompose(
                      dispatchTeamRequest,
                      always({
                        type: 'SetDeaNum',
                        payload: null,
                      })
                    )
                  ),
                  (a: string) => (a === '' ? right(void 0) : left(void 0))
                )
              ),
              ltap(setDeaRegistrationSearchInput),
              ltrim
            )}
            options={lcompose(
              uniq,
              lmap<IDeaRegistration, string>(pathOr('', 'deaNum'))
            )(deaRegistrations)}
            onChange={lcompose(
              () => setSerialNumberInput(''),
              () => setEmailInput(''),
              value =>
                dispatchTeamRequest({ type: 'SetDeaNum', payload: value })
            )}
            noOptionsText={'No results...'}
          />
          <FilterLabel id="serialNumber">
            Certificate Serial Number:
          </FilterLabel>
          <StyledAutocomplete2
            value={teamRequestState.serialNumber}
            labelledby="serialNumber"
            inputValue={serialNumberInput}
            onInputChange={lcompose(
              ltap(
                lcompose(
                  emap(
                    lcompose(
                      dispatchTeamRequest,
                      always({
                        type: 'SetSerialNumber',
                        payload: null,
                      })
                    )
                  ),
                  (a: string) => (a === '' ? right(void 0) : left(void 0))
                )
              ),
              ltap(setSerialNumberInput),
              ltrim
            )}
            options={serialNumbers}
            onChange={lcompose(
              () => setDeaRegistrationSearchInput(''),
              () => setEmailInput(''),
              value =>
                dispatchTeamRequest({
                  type: 'SetSerialNumber',
                  payload: value,
                })
            )}
            noOptionsText={
              serialNumberInput.length < searchEmailsMinLength
                ? `Please enter minimum ${searchEmailsMinLength} leading characters to initiate search...`
                : 'No results...'
            }
          />
        </FilterWrapper>
      </SectionWrapper>

      <SectionWrapper>
        <SubmitActions>
          <span>Revoke List</span>

          {!matchesGridBreakpoint && (
            <Checkbox
              color="primary"
              onClick={() =>
                dispatchTeamRequest({
                  type: 'ToggleUserRole',
                  payload: 'all',
                })
              }
              checked={deaNumUsersAllSelected({
                deaNumUsers: teamRequestState.deanumUsers,
                selectedUserRoleKeys: teamRequestState.selectedUserRoleKeys,
              })}
            />
          )}

          <Button
            area-label="clear revoke list"
            variant="contained"
            color="secondary"
            onClick={flow(
              () =>
                dispatchTeamRequest({ type: 'SetDeaNumUsers', payload: [] }),
              () => dispatchTeamRequest({ type: 'SetEmail', payload: null }),
              () => dispatchTeamRequest({ type: 'SetDeaNum', payload: null }),
              () =>
                dispatchTeamRequest({
                  type: 'SetSerialNumber',
                  payload: null,
                }),
              clearFirstNameLastName
            )}>
            Clear
          </Button>
        </SubmitActions>
        {flow(deaNumUsers, lisEmpty)(teamRequestState) && (
          <p>Revoke list is empty.</p>
        )}
        {deaNumUsers(teamRequestState).length > pageSize && (
          <Paginatio
            count={Math.ceil(deaNumUsers(teamRequestState).length / pageSize)}
            page={page}
            onChange={(z, page) => setPage(page)}
            shape="rounded"
          />
        )}

        {flow(deaNumUsers, lnegate(lisEmpty))(teamRequestState) &&
          (matchesGridBreakpoint ? (
            <UserRoles2d
              deaNumUsers={deaNumUsers(teamRequestState).slice(
                (page - 1) * pageSize,
                page * pageSize
              )}
              selectedUserRoleKeys={lcompose(
                lpropOr([], 'selectedUserRoleKeys')
              )(teamRequestState)}
              enableAll
              dispatchTeamRequest={dispatchTeamRequest}
            />
          ) : (
            <UserRolesStack
              deaNumUsers={deaNumUsers(teamRequestState).slice(
                (page - 1) * pageSize,
                page * pageSize
              )}
              selectedUserRoleKeys={lcompose(
                lpropOr([], 'selectedUserRoleKeys')
              )(teamRequestState)}
              dispatchTeamRequest={dispatchTeamRequest}
              properties={properties}
            />
          ))}

        {deaNumUsers(teamRequestState).length > pageSize && (
          <Paginatio
            count={Math.ceil(deaNumUsers(teamRequestState).length / pageSize)}
            page={page}
            onChange={(z, page) => setPage(page)}
            shape="rounded"
          />
        )}
      </SectionWrapper>

      <StatusSaveRequst apiCallStatus={saveRequestStatus(teamRequestState)} />

      <SubmitActions2>
        <Tooltip name="requestSubmit" tooltipValue={tooltipValue}>
          {disableOperationsHelpDsk || disableSubmit(teamRequestState) ? (
            <DisabledButton disabled variant="contained" color="primary">
              {saveRequestStatus(teamRequestState) === null ? (
                <FontAwesomeIcon icon={faSpinner} size="1x" fixedWidth spin />
              ) : (
                'submit'
              )}
            </DisabledButton>
          ) : (
            <Button onClick={handleConfirm} variant="contained" color="primary">
              {saveRequestStatus(teamRequestState) === null ? (
                <FontAwesomeIcon icon={faSpinner} size="1x" fixedWidth spin />
              ) : (
                'submit'
              )}
            </Button>
          )}
        </Tooltip>

        <TooltipTrigger
          name="requestSubmit"
          toggleTooltip={toggleTooltip}
          offTooltip={offTooltip}
        />
      </SubmitActions2>
      <article>
        <FieldRequiredIndicator /> fields are required.
      </article>
      <div role="status" aria-live="polite" className="vh">
        {
          /* {requestState.feedback} */
          // TODO
        }
      </div>
      {/* <pre>
        teamRequestState.firstName:
        {JSON.stringify(teamRequestState.firstName, null, 2)}
      </pre> */}
    </Wrapper>
  );
};

const Wrapper = styled(Container)(({ theme }) => ({
  '&>*+*': {
    marginTop: '3rem',
  },
}));

const Actions = styled('article')(({ theme }) => ({
  display: 'grid',
  gap: '0.5rem',
  alignItems: 'center',
  gridAutoRows: 'minmax(1rem,auto)',

  '@media (min-width: 33.375em)': {
    gridTemplateColumns: '100px  100px 28px 100px  28px',
  },
}));

const ActionLabel = styled('label')(({ theme }) => ({
  '@media (min-width: 33.375em)': {
    justifySelf: 'end',
  },
}));

const FilterLabel = styled('label')(({ theme }) => ({
  '@media (min-width: 33.375em)': {
    justifySelf: 'end',

    marginLeft: '5rem',
    marginTop: '0rem',
  },

  marginTop: '0.5rem',
}));

const SectionWrapper = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '1rem',
  },
}));

const FilterWrapper = styled('span')(({ theme }) => ({
  margin: '1.5rem',
  display: 'grid',
  gap: '0.5rem',

  '@media (min-width: 33.375em)': {
    display: 'grid',
    gridTemplateColumns: '200px  300px 200px 300px ',
    gap: '0.5rem',
  },
}));

const SubmitActions = styled('h3')(({ theme }) => ({
  '&>*+*': {
    marginLeft: '1rem !important',
  },
}));
const SubmitActions2 = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginLeft: '1rem',
  },
}));

const DisabledButton = styled(Button)(({ theme }) => ({
  '& span': { color: variables.brandPrimary },
}));

const Paginatio = styled(Pagination)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
}));
